import type { Transaction } from '../types/transactionTypes'

/** One month's worth of rows, in the order the server returned them. */
export interface TransactionMonthGroup {
  /** Stable `YYYY-MM` key for `v-for`. */
  key: string
  /** Header label the list renders, e.g. `"MARCH 2026"`. */
  label: string
  transactions: Transaction[]
}

const UNDATED_KEY = 'undated'
const UNDATED_LABEL = 'Undated'

// `Transaction.date` is already a display string ("18 Mar 2026"). Parsing it
// back is the fallback for rows whose wire payload carried no `createdAt` —
// `new Date("18 Mar 2026")` is understood by every engine we target.
function resolveDate(tx: Transaction): Date | null {
  const candidate = tx.createdAt || tx.date
  if (!candidate) return null
  const parsed = new Date(candidate)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()
}

/**
 * Bucket a flat activity feed into month sections for the wallet list.
 *
 * Order is preserved exactly as received — the server owns sorting, so a
 * "newest first" feed yields newest-first groups and a "lowest amount" feed
 * keeps its own order without this helper reshuffling anything.
 */
export function groupTransactionsByMonth(transactions: Transaction[]): TransactionMonthGroup[] {
  const groups: TransactionMonthGroup[] = []
  let currentMonth: string | null = null

  // Grouped by CONSECUTIVE run, not by collecting every March together. The
  // server owns ordering — under an amount sort, pulling a later March row up
  // into the first March heading would silently reorder the feed.
  for (const [index, tx] of transactions.entries()) {
    const date = resolveDate(tx)
    const month = date ? monthKey(date) : UNDATED_KEY
    const current = groups[groups.length - 1]

    if (current && currentMonth === month) {
      current.transactions.push(tx)
      continue
    }

    currentMonth = month
    groups.push({
      // A month can open more than one run, so the render key carries the run's
      // start index to stay unique for `v-for`.
      key: `${month}-${index}`,
      label: date ? monthLabel(date) : UNDATED_LABEL,
      transactions: [tx],
    })
  }

  return groups
}
